import {
  type GetFolderAssetsArgs,
  type SearchResult,
  useInfiniteScrollFromCache,
  useLazyGetFolderAssetsQuery,
} from '@carboncopyco/data';
import type { Entity, Folder } from '@carboncopyco/types';
import { SkeletonImageLoading } from '@carboncopyco/ui';
import { useEffect, useMemo, useRef } from 'react';
import { InfiniteList } from '../../../../InfiniteList';
import { useUrlState } from '../../../../utils';
import { AssetGridSection } from '../../AssetGridSection';
import { useWorkspacePageProps } from '../../WorkspacePageContext';
import { WorkspacePageControls } from '../../WorkspacePageControls';
import { WorkspacePageEmptySearch } from '../../WorkspacePageEmptySearch';
import {
  ALL_ENTITIES,
  INITIAL_URL_STATE,
  SHARED_FOLDER_ENTITIES,
} from '../../consts';
import type { UrlState } from '../../types';
import { FolderPageTableView } from '../FolderPageTableView';
import { FolderPageEmptyState } from './FolderPageEmptyState';

type DirtyRef = {
  isDirty: boolean;
  resetControls: () => void;
};

export const FolderPageBody = ({ folder }: { folder: Folder }) => {
  const { workspaceId, organizationId } = useWorkspacePageProps();
  const [urlState] = useUrlState<UrlState>(INITIAL_URL_STATE);
  const { viewMode, entities, creators, query, sort } = urlState;
  const dirtyRef = useRef<DirtyRef>({
    isDirty: false,
    resetControls: () => undefined,
  });

  const fetchArgs = useMemo<GetFolderAssetsArgs>(
    () => ({
      organizationId,
      workspaceId,
      creators: creators ? creators?.split(',') : undefined,
      sort,
      deletedEntities: false,
      entities: entities
        ? (entities.split(',') as Entity[])
        : ((folder?.readOnly
            ? SHARED_FOLDER_ENTITIES
            : ALL_ENTITIES) as Entity[]),
      query: query && query,
      folderId: folder?.id,
      limit: 30,
    }),
    [workspaceId, organizationId, entities, creators, query, sort, folder]
  );
  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
  } = useInfiniteScrollFromCache<SearchResult, GetFolderAssetsArgs>(
    useLazyGetFolderAssetsQuery,
    fetchArgs
  );
  const isDirty = dirtyRef?.current?.isDirty;
  const isEmpty = !items?.length && !isLoading && !isLoadingBatch;

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs, fetchFirstBatch]);

  if (isEmpty && !isDirty) {
    return <FolderPageEmptyState />;
  }

  return (
    <>
      <WorkspacePageControls
        ref={dirtyRef}
        readOnly={folder?.readOnly}
      />
      {isEmpty && isDirty ? (
        <WorkspacePageEmptySearch
          resetControls={dirtyRef.current.resetControls}
        />
      ) : (
        <div className="w-full flex justify-center overflow-y-hidden flex-auto py-5">
          <div className="w-ds-page h-full flex overflow-y-hidden relative">
            {viewMode === 'tiles' ? (
              <InfiniteList
                error={
                  error && {
                    message: 'Error fetching assets',
                  }
                }
                isLoading={isLoading}
                isLoadingBatch={isLoadingBatch}
                loader={
                  <SkeletonImageLoading
                    cols={4}
                    rows={4}
                  />
                }
                onReachBottom={fetchNextBatch}
              >
                <AssetGridSection
                  items={items}
                  inFolder
                />
              </InfiniteList>
            ) : (
              <FolderPageTableView
                items={items as SearchResult[]}
                isLoading={isLoading}
                isLoadingBatch={isLoadingBatch}
                error={error}
                fetchNextBatch={fetchNextBatch}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
